/**
 * Master Admin → Page Performance (Phase 2, "See it").
 *
 * How fast every page of the app opens for each company, measured on every
 * page open by the app itself. The frame: title, the "Master Admin only"
 * scope line with the retention line, the "How Page Performance works"
 * guide panel (collapse + close, remembered in this browser), the company /
 * period / compare filters (in the URL), the Live badge (30 s refetch while
 * the tab is visible, ⟳ at once), and the tab bar (`?tab=`):
 * Overview · Companies (all companies only) · Pages · Page Detail
 * (`?page=/route`, only while a page is open).
 *
 * Reads are cross-tenant by design and Master Admin only on the server; the
 * company filter is a filter, not a security boundary. All server access
 * lives in lib/page-perf/api-client.js.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, Info, Lightbulb, ShieldCheck, Timer, X } from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { FilterRow } from '../../components/filters/FilterRow'
import { MeasuresGuideModal } from '../../components/page-perf/MeasuresGuideModal'
import { LoadTimeHelpModal } from '../../components/page-perf/LoadTimeHelpModal'
import { PerfOverviewTab } from '../../components/page-perf/PerfOverviewTab'
import { PerfCompaniesTab } from '../../components/page-perf/PerfCompaniesTab'
import { PerfPagesTab } from '../../components/page-perf/PerfPagesTab'
import { PerfDetailTab } from '../../components/page-perf/PerfDetailTab'
import { formatNumber } from '../../lib/format'
import { useDisplayPref } from '../../lib/display-prefs'
import {
  LIVE_MS,
  PERF_KEY,
  useLiveStatus,
  useOrganisationOptions,
  viewerTimeZone,
} from '../../lib/page-perf/api-client'
import {
  COMPARES,
  PERIODS,
  buildUrlSearch,
  nextUrlState,
  normaliseLimits,
  pageNameForRoute,
  parseUrlState,
  periodLabel,
} from '../../lib/page-perf/view-model'

/*
 * The guide panel's two states are remembered in localStorage — they follow
 * the BROWSER, not the person: a second machine shows the open panel again,
 * and clearing site data resets it. A convenience, not a setting (the
 * spec's own trade-off; no endpoint for two booleans).
 */
const GUIDE_KEY = 'taskmagix.pagePerf.guide'

function readGuide() {
  try {
    const v = JSON.parse(localStorage.getItem(GUIDE_KEY) || '{}')
    return { collapsed: v.collapsed === true, closed: v.closed === true }
  } catch {
    return { collapsed: false, closed: false }
  }
}

function writeGuide(v) {
  try {
    localStorage.setItem(GUIDE_KEY, JSON.stringify(v))
  } catch {
    /* private mode / quota — the choice just won't stick */
  }
}

function GuidePanel({ guide, setGuide, onMeasures }) {
  if (guide.closed) return null
  return (
    <section
      aria-label="How Page Performance works"
      className="mb-4 min-w-0 rounded-xl border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] px-4 py-3"
    >
      <div className="flex items-center gap-2">
        <Lightbulb size={16} className="shrink-0 text-[color:var(--color-accent-strong)]" aria-hidden="true" />
        <h2 className="m-0 flex-1 font-display text-sm font-semibold text-[color:var(--color-ink-strong)]">How Page Performance works</h2>
        <button
          type="button"
          onClick={() => setGuide({ ...guide, collapsed: !guide.collapsed })}
          aria-expanded={!guide.collapsed}
          aria-label={guide.collapsed ? 'Expand the guide' : 'Collapse the guide'}
          title={guide.collapsed ? 'Expand' : 'Collapse'}
          className="inline-flex min-h-[28px] min-w-[28px] cursor-pointer items-center justify-center rounded-md text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card)] hover:text-[color:var(--color-ink-strong)]"
        >
          {guide.collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </button>
        <button
          type="button"
          onClick={() => setGuide({ ...guide, closed: true })}
          aria-label="Close the guide"
          title="Close — remembered in this browser"
          className="inline-flex min-h-[28px] min-w-[28px] cursor-pointer items-center justify-center rounded-md text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card)] hover:text-[color:var(--color-ink-strong)]"
        >
          <X size={16} />
        </button>
      </div>
      {!guide.collapsed ? (
        <div className="mt-2">
          <ul className="m-0 list-disc space-y-1 pl-5 text-[13px] leading-relaxed text-[color:var(--color-ink)]">
            <li>Every page a signed-in company member opens is measured by the app itself and stored with that company — nothing to install, and one company&rsquo;s figures never include another&rsquo;s.</li>
            <li><b>Company filter</b> — view all companies or narrow to one; every tab follows it, as do the period and the comparison.</li>
            <li><b>Pages</b> — the app&rsquo;s own pages with opens, people, Average, P50 / P95 / P99, TTFB, LCP and errors — click a row for the page view.</li>
            <li><b>Page view</b> — the six load-time figures in plain words, the browser figures, the timeline, who and how, the API calls behind the page and the same page in other companies.</li>
            <li>A page&rsquo;s colour is the worst of its P95 load time, TTFB, LCP and failed requests against their limits; a company&rsquo;s colour is its worst page.</li>
            <li>Raw opens are kept 30 days; daily history 13 months. People are hashed per day, and no query strings, form values or personal data are recorded.</li>
          </ul>
          <button
            type="button"
            onClick={onMeasures}
            className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-[color:var(--color-accent-border)] bg-[color:var(--color-card)] px-3 py-1.5 text-sm font-medium text-[color:var(--color-accent-strong)] hover:bg-[color:var(--color-card-hover)]"
          >
            <Info size={14} /> How Page Performance measures things
          </button>
          <p className="m-0 mt-2 text-[11px] text-[color:var(--color-ink-subtle)]">Collapsing or closing this panel is remembered in this browser only.</p>
        </div>
      ) : null}
    </section>
  )
}

function LiveBadge({ updatedAt, fetching, onRefresh }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const secs = updatedAt ? Math.max(0, Math.round((now - updatedAt) / 1000)) : null
  const ago =
    secs == null ? 'waiting for the first figures' : secs < 60 ? `updated ${formatNumber(secs)} s ago` : `updated ${formatNumber(Math.round(secs / 60))} min ago`
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-[color:var(--color-ink-muted)]"
      title={`Refreshes every ${LIVE_MS / 1000} s while this browser tab is open and visible — a page someone opens appears within about half a minute.`}
    >
      <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[color:var(--color-success)]" />
      <b className="text-[color:var(--color-ink-strong)]">Live</b>
      <span aria-live="polite">· {ago}</span>
      <FilterRow.Refresh onClick={onRefresh} loading={fetching} title="Refresh now" />
    </span>
  )
}

const TAB_LABEL = { overview: 'Overview', organizations: 'Companies', pages: 'Pages', detail: 'Page Detail' }

export function PagePerformancePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const state = useMemo(() => parseUrlState(searchParams), [searchParams])
  const queryClient = useQueryClient()
  const { updatedAt, fetching, refreshNow } = useLiveStatus()
  const tenants = useOrganisationOptions()

  const [guide, setGuideState] = useState(readGuide)
  const setGuide = useCallback((v) => {
    setGuideState(v)
    writeGuide(v)
  }, [])

  const [guideModal, setGuideModal] = useState(null) // { section, limits } | null
  const [loadHelp, setLoadHelp] = useState(null) // page figures | null

  const go = useCallback(
    (patch) => {
      const next = nextUrlState(parseUrlState(searchParams), patch)
      setSearchParams(new URLSearchParams(buildUrlSearch(next)))
    },
    [searchParams, setSearchParams],
  )

  // Keep the URL canonical (e.g. ?tab=organizations with one company picked
  // becomes the Overview) so a copied link says what the screen shows.
  useEffect(() => {
    const canonical = buildUrlSearch(state)
    const current = searchParams.toString() ? `?${searchParams.toString()}` : ''
    if (canonical !== current) setSearchParams(new URLSearchParams(canonical), { replace: true })
  }, [state, searchParams, setSearchParams])

  /** The latest `limits` any Page Performance read returned (for the guide). */
  const latestLimits = useCallback(() => {
    for (const [, data] of queryClient.getQueriesData({ queryKey: [PERF_KEY] })) {
      if (data?.limits) return normaliseLimits(data.limits)
    }
    return normaliseLimits(null)
  }, [queryClient])

  const openGuide = useCallback((section = 'how') => setGuideModal({ section, limits: latestLimits() }), [latestLimits])
  const openLoadHelp = useCallback(
    (row) => setLoadHelp({ ...row, loads: row.measuredLoads ?? row.loads }),
    [],
  )

  const companyOptions = useMemo(() => {
    const list = (tenants.data?.data ?? []).map((t) => ({ value: t.id, label: t.name }))
    list.sort((a, b) => a.label.localeCompare(b.label))
    const known = list.some((o) => o.value === state.organization)
    return [
      { value: 'all', label: 'All companies' },
      ...list,
      ...(state.organization !== 'all' && !known ? [{ value: state.organization, label: 'Selected company' }] : []),
    ]
  }, [tenants.data, state.organization])
  const companyName =
    state.organization === 'all' ? null : (companyOptions.find((o) => o.value === state.organization)?.label ?? null)

  // The person's own zone (My Preferences). Reading it here re-renders the
  // page when it changes, and `tz` in the scope (and in every query key, see
  // api-client.js) makes each tab refetch at once in the new zone.
  const personalZone = useDisplayPref('timezone')
  const tz = useMemo(() => viewerTimeZone(), [personalZone]) // eslint-disable-line react-hooks/exhaustive-deps
  const scope = useMemo(
    () => ({ organization: state.organization, period: state.period, compare: state.compare, tz }),
    [state.organization, state.period, state.compare, tz],
  )
  const ctx = useMemo(
    () => ({
      scope,
      state,
      go,
      slot: state.period === 'today' || state.period === '24h' ? 'hour' : 'day',
      companyName,
      openGuide,
      openLoadHelp,
    }),
    [scope, state, go, companyName, openGuide, openLoadHelp],
  )

  const tabs = ['overview', ...(state.organization === 'all' ? ['organizations'] : []), 'pages', ...(state.page ? ['detail'] : [])]

  return (
    // `relative` contains the visually-hidden live region below: without a
    // positioned ancestor it is placed against the page and stretches the
    // window past the layout (the shell's <main> is not positioned).
    <div className="relative min-w-0">
      <Breadcrumb current="Page Performance" homePath="/super-admin/dashboard" />

      <div className="mb-3 min-w-0">
        <h1 className="m-0 flex flex-wrap items-center gap-3 font-display text-3xl text-[color:var(--color-ink-strong)]">
          <Timer size={22} className="text-[color:var(--color-accent-strong)]" aria-hidden="true" />
          Page Performance
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          How fast every page of the app opens for each company — measured on every page open by the app itself. Nothing to install.
        </p>
        <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[color:var(--color-ink-muted)]">
          <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] px-2 py-0.5 font-semibold text-[color:var(--color-accent-strong)]">
            <ShieldCheck size={12} /> Master Admin only
          </span>
          <span>Every open is stored with its company; one company&rsquo;s figures never include another&rsquo;s · raw opens kept 30 days · daily history 13 months.</span>
          {guide.closed ? (
            <button type="button" onClick={() => setGuide({ collapsed: false, closed: false })} className="cursor-pointer font-medium text-[color:var(--color-accent-strong)] hover:underline">
              Show the guide
            </button>
          ) : null}
        </p>
      </div>

      <GuidePanel guide={guide} setGuide={setGuide} onMeasures={() => openGuide('how')} />

      <FilterRow>
        <FilterRow.Select
          ariaLabel="Company"
          value={state.organization}
          onChange={(v) => go({ organization: v || 'all' })}
          options={companyOptions}
        />
        <FilterRow.Select ariaLabel="Period" value={state.period} onChange={(v) => go({ period: v })} options={PERIODS.map((p) => ({ value: p.value, label: p.label }))} />
        <FilterRow.Select ariaLabel="Compare" value={state.compare} onChange={(v) => go({ compare: v })} options={COMPARES.map((c) => ({ value: c.value, label: c.label }))} />
        <FilterRow.Spacer />
        <LiveBadge updatedAt={updatedAt} fetching={fetching} onRefresh={refreshNow} />
      </FilterRow>

      <div role="tablist" aria-label="Page Performance tabs" className="mb-4 flex min-w-0 gap-1 overflow-x-auto overflow-y-hidden border-b border-[color:var(--color-rule)]">
        {tabs.map((t) => {
          const active = state.tab === t
          return (
            <button
              key={t}
              type="button"
              role="tab"
              id={`pp-tab-${t}`}
              aria-selected={active}
              aria-controls={`pp-panel-${t}`}
              onClick={() => go({ tab: t, ...(t === 'detail' ? { page: state.page } : {}) })}
              className={
                '-mb-px inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ' +
                (active
                  ? 'border-[color:var(--color-accent)] text-[color:var(--color-accent-strong)]'
                  : 'border-transparent text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]')
              }
            >
              {TAB_LABEL[t]}
              {t === 'detail' && state.page ? (
                <span className="max-w-[160px] truncate font-mono text-xs font-normal text-[color:var(--color-ink-subtle)]" title={state.page}>
                  {state.page}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>

      <div role="tabpanel" id={`pp-panel-${state.tab}`} aria-labelledby={`pp-tab-${state.tab}`} className="min-w-0">
        {state.tab === 'overview' ? <PerfOverviewTab ctx={ctx} /> : null}
        {state.tab === 'organizations' ? <PerfCompaniesTab ctx={ctx} /> : null}
        {state.tab === 'pages' ? <PerfPagesTab ctx={ctx} /> : null}
        {state.tab === 'detail' ? <PerfDetailTab ctx={ctx} /> : null}
      </div>

      <MeasuresGuideModal
        open={Boolean(guideModal)}
        section={guideModal?.section}
        limits={guideModal?.limits}
        onClose={() => setGuideModal(null)}
      />
      <LoadTimeHelpModal
        open={Boolean(loadHelp)}
        page={loadHelp}
        scopeText={`${periodLabel(state.period)}${companyName ? ` · ${companyName}` : ' · all companies'}`}
        usersExact={loadHelp?.usersExact === true}
        onClose={() => setLoadHelp(null)}
      />
      <span className="sr-only" aria-live="polite">
        {state.tab === 'detail' && state.page ? `Page Detail: ${pageNameForRoute(state.page)}` : TAB_LABEL[state.tab]}
      </span>
    </div>
  )
}
